import React, { useCallback, useEffect, useState } from "react";
import { maintenanceApi, ProcSim, ProcExpiringSim, ProcUnregisteredSim } from "../../../api/maintenance";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { fmtDate, errMsg } from "./procUtils";
import SimDrawer from "./SimDrawer";
import { Plus, Search, AlertTriangle, Radio } from "lucide-react";

interface Props {
  refreshKey: number;
  onChanged: (msg?: string) => void;
  onError: (msg: string) => void;
}

const EXPIRY_WINDOW = 7;

/** Colour the expiry by urgency — this is the whole point of the tab. */
export const expiryPill = (daysLeft: number | null, validUntil: string | null) => {
  if (validUntil === null || daysLeft === null)
    return { cls: "grey", label: "never recharged" };
  if (daysLeft < 0) return { cls: "red", label: `expired ${Math.abs(daysLeft)}d ago` };
  if (daysLeft <= 3) return { cls: "red", label: `${daysLeft}d left` };
  if (daysLeft <= EXPIRY_WINDOW) return { cls: "orange", label: `${daysLeft}d left` };
  return { cls: "green", label: `${daysLeft}d left` };
};

/**
 * SIMs and their recharges.
 *
 * A prepaid SIM quietly running out is a real outage source: the charger drops
 * off and nobody connects the two events. Sorting soonest-to-expire first, and
 * banner-ing anything inside the window, is what makes that visible in advance.
 */
const SimsTab: React.FC<Props> = ({ refreshKey, onChanged, onError }) => {
  const [sims, setSims] = useState<ProcSim[]>([]);
  const [expiring, setExpiring] = useState<ProcExpiringSim[]>([]);
  const [unreg, setUnreg] = useState<ProcUnregisteredSim[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [view, setView] = useState<"registered" | "unknown">("registered");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ msisdn: "", iccid: "", provider: "", plan: "" });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const canEdit = canEditMaintenance();

  const fetchSims = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, un] = await Promise.all([
        maintenanceApi.procGetSims({
          search: search || undefined,
          unassigned_only: unassignedOnly || undefined,
          page_size: 200,
        }),
        maintenanceApi.procGetExpiringSims(EXPIRY_WINDOW, true),
        maintenanceApi.procGetUnregisteredSims(),
      ]);
      setSims(s.data.items);
      setExpiring(e.data);
      setUnreg(un.data);
    } catch (err: any) {
      onError(errMsg(err, "Failed to load SIMs"));
    } finally {
      setLoading(false);
    }
  }, [search, unassignedOnly, onError]);

  useEffect(() => {
    fetchSims();
  }, [fetchSims, refreshKey]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.msisdn.trim()) {
      setFormError("Number is required");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await maintenanceApi.procCreateSim({
        msisdn: form.msisdn.trim(),
        iccid: form.iccid.trim() || null,
        provider: form.provider.trim() || null,
        plan: form.plan.trim() || null,
      });
      setAdding(false);
      setForm({ msisdn: "", iccid: "", provider: "", plan: "" });
      fetchSims();
      onChanged("SIM added");
    } catch (err: any) {
      setFormError(errMsg(err, "Failed to add SIM"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Sub-views: the ones we track, and the ones the chargers are telling us
          about that we don't. The second is a worklist, not a nag. */}
      <div className="proc-tabs" style={{ marginBottom: 14 }}>
        <button
          className={view === "registered" ? "active" : ""}
          onClick={() => setView("registered")}
        >
          Tracked SIMs{sims.length ? ` (${sims.length})` : ""}
        </button>
        <button className={view === "unknown" ? "active" : ""} onClick={() => setView("unknown")}>
          Unknown{unreg.length ? ` (${unreg.length})` : ""}
        </button>
      </div>

      {view === "registered" && expiring.length > 0 && (
        <div className="proc-banner warn">
          <AlertTriangle size={16} />
          <span>
            <b>{expiring.length}</b> SIM{expiring.length === 1 ? "" : "s"} expiring within{" "}
            {EXPIRY_WINDOW} days (or already expired / never recharged) —{" "}
            {expiring
              .slice(0, 4)
              .map((s) => `${s.msisdn}${s.device_unique_id ? ` (${s.device_unique_id})` : ""}`)
              .join(", ")}
            {expiring.length > 4 ? `, +${expiring.length - 4} more` : ""}. Recharge these before the
            chargers drop off.
          </span>
        </div>
      )}

      {view === "unknown" && (
        <>
          <div className="proc-banner info">
            <Radio size={16} />
            <span>
              These chargers report a SIM over OCPP that we hold no record for. OCPP carries the
              ICCID but <b>never the mobile number</b> — and the number is what gets recharged — so
              they can't be added automatically. Everything below is here to help you work out
              which physical SIM it is; add the number and it moves to <b>Tracked SIMs</b> and
              links itself to the device on its next boot.
            </span>
          </div>

          <table className="maint-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Where</th>
                <th>Status</th>
                <th>ICCID</th>
                <th>Likely operator</th>
                <th>Hardware</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {unreg.map((x) => (
                <tr key={x.procurement_device_id}>
                  <td>
                    <span className="proc-mono">{x.unique_id}</span>
                    {x.display_name && <div className="proc-mini">{x.display_name}</div>}
                  </td>
                  <td>
                    {x.station_name || <span style={{ color: "#bbb" }}>not deployed</span>}
                    {x.org_name && <div className="proc-mini">{x.org_name}</div>}
                  </td>
                  <td>
                    <span className={`proc-pill ${x.is_online ? "green" : "red"}`}>
                      {x.is_online ? "online" : "offline"}
                    </span>
                    <div className="proc-mini">
                      {x.current_state || x.stage}
                      {x.last_active ? ` · seen ${fmtDate(x.last_active)}` : ""}
                    </div>
                  </td>
                  <td className="proc-mono" style={{ fontSize: 11 }}>
                    {x.iccid}
                    {x.imsi && <div className="proc-mini">IMSI {x.imsi}</div>}
                  </td>
                  <td>
                    {x.likely_provider ? (
                      <span className="proc-pill grey">{x.likely_provider}</span>
                    ) : (
                      <span style={{ color: "#bbb" }}>—</span>
                    )}
                  </td>
                  <td className="proc-mini">
                    {[x.cp_vendor, x.cp_model].filter(Boolean).join(" ") || "—"}
                    {x.serial_no && <div>sn {x.serial_no}</div>}
                  </td>
                  {canEdit && (
                    <td>
                      <button
                        className="maint-btn-primary maint-btn-small"
                        onClick={() => {
                          setForm({
                            msisdn: "",
                            iccid: x.iccid || "",
                            provider:
                              x.likely_provider && !x.likely_provider.startsWith("MCC")
                                ? x.likely_provider
                                : "",
                            plan: "",
                          });
                          setAdding(true);
                        }}
                      >
                        Add number
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {unreg.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 7 : 6} className="maint-empty">
                    Nothing unknown — every charger reporting a SIM has a record.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {view === "registered" && (
        <div className="maint-toolbar">
          <form
            className="maint-search"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput.trim());
            }}
          >
            <Search size={16} color="#999" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search number, ICCID, provider or device ID…"
            />
          </form>
          <label style={{ fontSize: 13, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              className="proc-cbx"
              checked={unassignedOnly}
              onChange={(e) => setUnassignedOnly(e.target.checked)}
            />
            Spare only
          </label>
          {canEdit && (
            <button
              className="maint-btn-primary"
              onClick={() => {
                setForm({ msisdn: "", iccid: "", provider: "", plan: "" });
                setAdding(true);
              }}
            >
              <Plus size={16} /> Add SIM
            </button>
          )}
        </div>
      )}

      {view === "registered" &&
        (loading ? (
        <p className="maint-loading">Loading…</p>
      ) : (
        <table className="maint-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Provider</th>
              <th>Fitted to</th>
              <th>Station</th>
              <th>Expires</th>
              <th>Recharges</th>
              <th>Spent</th>
            </tr>
          </thead>
          <tbody>
            {sims.map((s) => {
              const p = expiryPill(s.days_left, s.valid_until);
              return (
                <tr key={s.id} className="clickable" onClick={() => setOpenId(s.id)}>
                  <td className="proc-mono">{s.msisdn}</td>
                  <td>{s.provider || <span style={{ color: "#bbb" }}>—</span>}</td>
                  <td>
                    {s.device_unique_id ? (
                      <span className="proc-mono">{s.device_unique_id}</span>
                    ) : (
                      <span className="proc-pill grey">spare</span>
                    )}
                  </td>
                  <td>{s.station_name || <span style={{ color: "#bbb" }}>—</span>}</td>
                  <td>
                    <span className={`proc-pill ${p.cls}`}>{p.label}</span>
                    {s.valid_until && (
                      <div className="proc-mini">{fmtDate(s.valid_until)}</div>
                    )}
                  </td>
                  <td>{s.recharge_count}</td>
                  <td>{s.total_spent != null ? `₹${s.total_spent.toLocaleString()}` : "—"}</td>
                </tr>
              );
            })}
            {sims.length === 0 && (
              <tr>
                <td colSpan={7} className="maint-empty">
                  {search || unassignedOnly
                    ? "No SIMs match."
                    : unreg.length
                    ? `No SIMs tracked yet — ${unreg.length} charger${unreg.length === 1 ? " is" : "s are"} reporting one. See the Unknown tab.`
                    : "No SIMs yet — add one, then fit it to a device."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        ))}

      {adding && (
        <div className="maint-form-overlay" onClick={() => setAdding(false)}>
          <div className="maint-form-card" onClick={(e) => e.stopPropagation()}>
            <div className="maint-form-header">
              <h3>Add SIM</h3>
              <button className="maint-btn-icon" onClick={() => setAdding(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleAdd}>
              <div className="maint-form-row">
                <label>
                  Number *
                  <input
                    value={form.msisdn}
                    onChange={(e) => setForm({ ...form, msisdn: e.target.value })}
                    placeholder="+9198…"
                  />
                </label>
                <label>
                  Provider
                  <input
                    value={form.provider}
                    onChange={(e) => setForm({ ...form, provider: e.target.value })}
                    placeholder="Airtel / Jio / Vi / BSNL"
                  />
                </label>
              </div>
              <label>
                ICCID
                <input
                  value={form.iccid}
                  onChange={(e) => setForm({ ...form, iccid: e.target.value })}
                  placeholder="89910000…"
                />
              </label>
              <div className="proc-note" style={{ marginTop: 0 }}>
                Chargers report their ICCID over OCPP — fill this in and the SIM links itself to
                the right device on its next boot, with no manual assignment.
              </div>
              <label>
                Plan
                <input
                  value={form.plan}
                  onChange={(e) => setForm({ ...form, plan: e.target.value })}
                  placeholder="1GB / 28 days"
                />
              </label>
              {formError && <p className="maint-form-error">{formError}</p>}
              <div className="maint-form-actions">
                <button type="button" className="maint-btn-secondary" onClick={() => setAdding(false)}>
                  Cancel
                </button>
                <button type="submit" className="maint-btn-primary" disabled={saving}>
                  {saving ? "Saving…" : "Add SIM"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {openId !== null && (
        <SimDrawer
          simId={openId}
          onClose={() => setOpenId(null)}
          onChanged={(msg) => {
            fetchSims();
            onChanged(msg);
          }}
        />
      )}
    </>
  );
};

export default SimsTab;
